/**
 * useTokenExpiration Hook
 *
 * Monitors JWT token expiration and redirects to login when tokens expire.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import type { RootState } from '../store';
import { isTokenExpired, getTimeUntilExpiration } from '../utils/tokenUtils';
import { expireSession } from '../utils/authSession';

interface UseTokenExpirationOptions {
  enabled?: boolean;
  checkInterval?: number;
  inactivityThreshold?: number;
  warningBuffer?: number;
  logoutBuffer?: number;
  preserveIntendedRoute?: boolean;
}

interface TokenExpirationState {
  isExpired: boolean;
  isWarning: boolean;
  isInactive: boolean;
  minutesUntilExpiration: number;
  minutesSinceActivity: number;
  expirationDisplay: string;
  checkExpiration: (force?: boolean) => void;
  refreshSession: () => Promise<void>;
  resetActivity: () => void;
}

export const useTokenExpiration = (options: UseTokenExpirationOptions = {}): TokenExpirationState => {
  const {
    enabled = true,
    checkInterval = 1,
    inactivityThreshold = 2,
    warningBuffer = 5,
    logoutBuffer = 0,
  } = options;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { token, isAuthenticated } = useSelector((state: RootState) => state.auth);

  const [isExpired, setIsExpired] = useState(false);
  const [isWarning, setIsWarning] = useState(false);
  const [isInactive, setIsInactive] = useState(false);
  const [minutesUntilExpiration, setMinutesUntilExpiration] = useState(0);
  const [minutesSinceActivity, setMinutesSinceActivity] = useState(0);
  const [expirationDisplay, setExpirationDisplay] = useState('');

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const activityIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastCheckRef = useRef<number>(0);
  const lastActivityRef = useRef<number>(Date.now());
  const hasRunInitialCheck = useRef(false);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setMinutesSinceActivity(0);
    setIsInactive(false);

    if (isWarning && token && !isTokenExpired(token, logoutBuffer)) {
      setIsWarning(false);
    }
  }, [isWarning, token, logoutBuffer]);

  const handleTokenExpiration = useCallback(() => {
    expireSession(dispatch, navigate, location);
  }, [dispatch, navigate, location]);

  const checkExpiration = useCallback(
    (force = false) => {
      if (!enabled || !token || !isAuthenticated) {
        setIsExpired(false);
        setIsWarning(false);
        setIsInactive(false);
        setMinutesUntilExpiration(0);
        setExpirationDisplay('');
        return;
      }

      const now = Date.now();
      if (!force && now - lastCheckRef.current < 30000) {
        return;
      }
      lastCheckRef.current = now;

      const minutesLeft = getTimeUntilExpiration(token);
      const expired = isTokenExpired(token, logoutBuffer);
      const timeSinceActivity = Math.floor((now - lastActivityRef.current) / (60 * 1000));
      const inactive = timeSinceActivity >= inactivityThreshold;
      const shouldWarn = !expired && minutesLeft <= warningBuffer && inactive;

      setMinutesUntilExpiration(minutesLeft);
      setMinutesSinceActivity(timeSinceActivity);
      setIsExpired(expired);
      setIsInactive(inactive);
      setIsWarning(shouldWarn);

      if (expired) {
        setExpirationDisplay('Sesión expirada');
        handleTokenExpiration();
      } else if (shouldWarn) {
        setExpirationDisplay(
          `Su sesión expirará en ${minutesLeft} minuto${minutesLeft !== 1 ? 's' : ''} si permanece inactivo`
        );
      } else if (minutesLeft <= 0) {
        setExpirationDisplay('Expirando...');
      } else {
        setExpirationDisplay('');
      }
    },
    [
      enabled,
      token,
      isAuthenticated,
      inactivityThreshold,
      warningBuffer,
      logoutBuffer,
      handleTokenExpiration,
    ]
  );

  const refreshSession = useCallback(async () => {
    resetActivity();
    checkExpiration(true);
  }, [checkExpiration, resetActivity]);

  useEffect(() => {
    if (!enabled || !token || !isAuthenticated) {
      return;
    }

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      resetActivity();
    };

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    activityIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const timeSinceActivity = Math.floor((now - lastActivityRef.current) / (60 * 1000));
      setMinutesSinceActivity(timeSinceActivity);
      setIsInactive(timeSinceActivity >= inactivityThreshold);
    }, 30000);

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });

      if (activityIntervalRef.current) {
        clearInterval(activityIntervalRef.current);
        activityIntervalRef.current = null;
      }
    };
  }, [enabled, token, isAuthenticated, inactivityThreshold, resetActivity]);

  useEffect(() => {
    if (!enabled || !token || !isAuthenticated) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      hasRunInitialCheck.current = false;
      return;
    }

    if (!hasRunInitialCheck.current) {
      hasRunInitialCheck.current = true;
      checkExpiration(true);
    }

    const intervalMs = checkInterval * 60 * 1000;
    intervalRef.current = setInterval(() => checkExpiration(false), intervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, token, isAuthenticated, checkInterval, checkExpiration]);

  return {
    isExpired,
    isWarning,
    isInactive,
    minutesUntilExpiration,
    minutesSinceActivity,
    expirationDisplay,
    checkExpiration,
    refreshSession,
    resetActivity,
  };
};
